import { useEffect, useState } from "react";
import { clearDrafts } from "./drafts";
import { Login } from "./components/Login";
import { AcceptInvite } from "./components/AcceptInvite";
import { PasswordLink } from "./components/PasswordLink";
import { Workspace } from "./components/Workspace";
import { SessionContext } from "./hooks";
import { MFASetupNeeded, Session, saveToken, savedToken } from "./session";
import { Api, ApiError } from "./sync/api";
import { MFAGate } from "./components/MFAGate";
import { t } from "./i18n";
import { call } from "./huddle";

// readFragment takes a session token or sign-in error that single sign-on
// left in the URL fragment, and removes it from the address bar.
function readFragment(): { token?: string; error?: string } {
  const params = new URLSearchParams(location.hash.slice(1));
  const token = params.get("token") ?? undefined;
  const error = params.get("sso_error") ?? undefined;
  if (token || error) history.replaceState(null, "", location.pathname + location.search);
  if (token) saveToken(token);
  return { token, error };
}

const fromSSO = readFragment();

// Confirming it is them through single sign-on ends in a window of its own,
// which tells the page that opened it how it went, and closes (ADR-0026).
{
  const params = new URLSearchParams(location.hash.slice(1));
  if (params.has("reauthed") || params.has("reauth_error")) {
    history.replaceState(null, "", location.pathname + location.search);
    if (window.opener) {
      (window.opener as Window).postMessage({ lantern: "reauth", ok: params.has("reauthed"), error: params.get("reauth_error") ?? undefined }, location.origin);
      window.close();
    }
  }
}

// An invitation's link: /invite/<token>.
const inviteToken = location.pathname.match(/^\/invite\/(lni_[A-Za-z0-9_-]+)$/)?.[1];
// A link to set a password, from an admin: /password/<token>.
const passwordToken = location.pathname.match(/^\/password\/(lnp_[A-Za-z0-9_-]+)$/)?.[1];

export function App() {
  const [token, setToken] = useState(() => fromSSO.token ?? savedToken());
  const [session, setSession] = useState<Session | null>(null);
  // A session that cannot start until two-step verification is set up.
  const [gate, setGate] = useState<Session | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(inviteToken);
  const [passwordLink, setPasswordLink] = useState(passwordToken);
  // Who just set their password, to sign in as.
  const [handle, setHandle] = useState("");

  useEffect(() => {
    if (!token) return;
    const s = new Session(token);
    let cancelled = false;
    // Groups can come to need two-step verification while one is signed in:
    // the server then refuses, and the client starts again, at the gate.
    Api.onMFASetup = () => {
      Api.onMFASetup = undefined;
      location.reload();
    };
    s.start().then(
      () => !cancelled && setSession(s),
      (err) => {
        if (cancelled) return;
        if (err instanceof MFASetupNeeded) {
          setGate(s);
          return;
        }
        if (err instanceof ApiError && err.status === 401) {
          saveToken(null);
          setToken(null);
        } else {
          setError(t("Could not reach the server."));
        }
      },
    );
    return () => {
      cancelled = true;
      s.stop();
      setSession(null);
      setGate(null);
    };
  }, [token, attempt]);

  function signedIn(token: string) {
    saveToken(token);
    setError("");
    setToken(token);
  }

  async function signOut() {
    await call.leave();
    await (session ?? gate)?.api.logout().catch(() => {});
    saveToken(null);
    clearDrafts();
    setToken(null);
  }

  if (invite) {
    return (
      <AcceptInvite
        token={invite}
        onSignedIn={(token) => {
          history.replaceState(null, "", "/");
          setInvite(undefined);
          signedIn(token);
        }}
      />
    );
  }
  if (passwordLink) {
    return (
      <PasswordLink
        token={passwordLink}
        onDone={(h) => {
          history.replaceState(null, "", "/");
          setPasswordLink(undefined);
          setHandle(h);
        }}
      />
    );
  }
  if (!token) return <Login onSignedIn={signedIn} ssoError={fromSSO.error} handle={handle} />;
  if (error) return <main className="placeholder">{error}</main>;
  if (gate) {
    return (
      <SessionContext.Provider value={gate}>
        <MFAGate onReady={() => setAttempt((n) => n + 1)} onSignOut={signOut} />
      </SessionContext.Provider>
    );
  }
  if (!session) return <main className="placeholder">{t("Connecting…")}</main>;
  return (
    <SessionContext.Provider value={session}>
      <Workspace onSignOut={signOut} />
    </SessionContext.Provider>
  );
}
